<template>
  <div class="chart">
    <VsLoader :visible="!!itemCoverageTotals.isLoading" />
    <template v-if="chartData">
      <ItemCoverageChart
        :chartData="chartData"
        :options="chartOptions"
        :height="height"
        :width="width"
        :styles="{
          maxWidth: '725px',
          margin: 'auto',
        }"
      />
    </template>
  </div>
</template>

<script>
import ItemCoverageChart from "./ItemCoverageChart";
import tooltipConfig from "@/misc/chartTooltip";

export default {
  components: {
    ItemCoverageChart,
  },
  props: {
    itemCoverageTotals: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chartData: null,
      height: 267,
      width: 720,
    };
  },
  computed: {
    isChartAvailable() {
      const chartCategories = [
        "successfully-parsed",
        "vs-optimization-in-progress",
        "missing-measurements",
        "no-measurements",
        "unsupported-category",
      ];
      let itemCoverageTotals = this.itemCoverageTotals;
      return chartCategories.every((chart) => itemCoverageTotals[chart]);
    },
    chartOptions() {
      const numberFormat = this.numberFormat;
      return {
        cutoutPercentage: 70,
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
            fontColor: "#052E2B",
            fontFamily: "'Noto Sans', 'Roboto', Arial, sans-serif",
            fontSize: 10.5,
            padding: 15,
          },
        },
        title: {
          display: true,
          text: this.$t("charts.itemCoverageChart.items", { total: this.total }),
          fontColor: "#052E2B",
          fontFamily: "'Noto Sans', 'Roboto', Arial, sans-serif",
          fontSize: 24,
          fontStyle: "bold",
        },
        tooltips: {
          ...tooltipConfig,
          displayColors: false,
          callbacks: {
            label: function (tooltipItem, data) {
              const index = tooltipItem.index;
              const value = data.datasets[0].data[index];
              return numberFormat(value).toString();
            },
          },
        },
      };
    },
    total() {
      return this.numberFormat(this.itemCoverageTotals["total"]);
    },
  },
  watch: {
    itemCoverageTotals() {
      this.setChartData();
    },
    isChartAvailable() {
      this.setChartData();
    },
  },
  created() {
    this.setChartData();
  },
  methods: {
    numberFormat(value, format = null) {
      return this.$options.filters.numberFormat(value, format);
    },
    async setChartData() {
      if (this.isChartAvailable) {
        this.chartData = await this.dataCollection();
      }
    },
    dataCollection() {
      let itemCoverageTotals = {
        datasets: [
          {
            data: [
              this.itemCoverageTotals["successfully-parsed"],
              this.itemCoverageTotals["vs-optimization-in-progress"],
              this.itemCoverageTotals["missing-measurements"],
              this.itemCoverageTotals["no-measurements"],
              this.itemCoverageTotals["unsupported-category"],
            ],
            backgroundColor: [
              "rgb(22,198,185)",
              "rgb(51,163,255)",
              "rgb(244,98,142)",
              "rgb(236,56,110)",
              "rgb(87,87,87)",
            ],
          },
        ],
        labels: [
          this.$t("charts.itemCoverageChart.successfullyParsed"),
          this.$t("charts.itemCoverageChart.vsOptimizationInProgress"),
          this.$t("charts.itemCoverageChart.missingMeasurements"),
          this.$t("charts.itemCoverageChart.noMeasurements"),
          this.$t("charts.itemCoverageChart.unsupportedCategory"),
        ],
      };
      return Promise.resolve(itemCoverageTotals);
    },
  },
};
</script>

<style scoped>
.chart {
  position: relative;
  padding: 40px 8px;
}
</style>
